import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../Footer/footer";
import Navbar from "../Navbar/navbar";
import LogOutPopup from "./modals/logOutPopup";
import TrackOrderPopup from "./modals/trackOrderPopup";
import AddressesSection from "./addressesSection";
import YourOrders from "./yourOrders";
import CouponsSection from "./couponsSection";
import { addNewAddress, setShippingAddress, fetchAddresses } from "../../Providers/addressProvider";
import OtpPopup from "./modals/otpPopup";
import FooterDoodles from "../Footer/footerDoodles";
import icons from "../../icons.config";
import { getUserProfileAPI } from "../../Providers/profileProvider";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { section } = useParams();
  const dispatch = useDispatch();
  const state = useSelector((state) => state); // Log full state
  console.log("Redux State:", state);

  const orders = useSelector((state) => state.orders.items || []);
  const shippingAddress = useSelector((state) => state.address?.savedAddresses[0]);

  const [emailVerified, setEmailVerified] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showTrackOrderPopup, setShowTrackOrderPopup] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [user, setUser] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const authToken = useSelector((state) => state.auth.authToken);

  useEffect(() => {
    if (authToken) {
      dispatch(fetchAddresses());
    } else {
      navigate("/login");
    }
  }, [authToken, dispatch, navigate]);

  // Section Mapping
  const sectionMapping = {
    undefined: "YOUR ORDERS",
    addresses: "Addresses",
    "coupons-offers": "Coupons & Offers",
  };

  const [activeSection, setActiveSection] = useState(sectionMapping[section] || "YOUR ORDERS");

  // Update URL based on active section
  useEffect(() => {
    const newPath =
      activeSection === "YOUR ORDERS"
        ? "/profile"
        : activeSection === "Addresses"
        ? "/profile/addresses"
        : "/profile/coupons-offers";
    navigate(newPath, { replace: true });
  }, [activeSection, navigate]);

  const [address, setAddress] = useState(() => {
    const storedAddress = JSON.parse(localStorage.getItem("userAddress"));
    return storedAddress || {
      firstName: shippingAddress?.firstName || user?.first_name || "",
      lastName: shippingAddress?.last_name || user?.last_name || "",
      email: shippingAddress?.email || user?.email_id || "",
      phone: shippingAddress?.phone || user?.mobile_number || "",
      address: shippingAddress?.address || "",
    };
  });

  useEffect(() => {
    if (!localStorage.getItem("userAddress") && (shippingAddress || user)) {
      const newAddress = {
        firstName: shippingAddress?.firstName || user?.first_name || "",
        lastName: shippingAddress?.lastName || user?.last_name || "",
        email: user?.email_id || "",
        phone: user?.mobile_number || "",
        address: shippingAddress?.address || "",
      };
      localStorage.setItem("userAddress", JSON.stringify(newAddress));
      setAddress(newAddress);
    }
    console.log("Updated Address State:", address);
  }, [shippingAddress, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!address.firstName || !address.lastName || !address.email || !address.phone || !address.address) {
      alert("All fields are required!");
      return;
    }
    try {
      await dispatch(addNewAddress(address));
      dispatch(setShippingAddress(address));
      localStorage.setItem("userAddress", JSON.stringify(address));
      setIsEditable(false);
      alert("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  const fullAddress = shippingAddress
    ? `${shippingAddress.main_street || ""},${shippingAddress.landmark || ""}, ${shippingAddress.city || ""}, ${shippingAddress.state || ""} - ${shippingAddress.postal_code || ""}, ${shippingAddress.country || ""}`
    : "";

  const handleVerifyClick = () => {
    setShowOtpModal(true);
  };

  const [error, setError] = useState(null);
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await getUserProfileAPI();
        if (response?.data) {
          setUser(response.data);
        } else {
          setError(response.error || "Unexpected response format");
          console.error("Error fetching user profile:", response);
        }
      } catch (error) {
        setError("Failed to fetch user profile");
        console.error("Error fetching user profile:", error);
      }
    };
    fetchUserProfile();
  }, []);

  return (
    <>
      <Navbar />

      <div
        className={`flex flex-col lg:flex-row gap-10 lg:gap-0 bg-tertiary relative top-16 lg:top-20 py-24 lg:px-20 xl:px-32 overflow-hidden ${
          showLogoutPopup || showTrackOrderPopup || showOtpModal
            ? "opacity-30 pointer-events-none backdrop-blur-sm"
            : "opacity-100"
        }`}
      >
        {/* Vertical Line */}
        <div className="hidden lg:block w-[2px] bg-secondary absolute left-1/3 top-0 bottom-0"></div>

        {/* Left Section */}
        <div className="lg:w-1/3 w-full bg-tertiary px-10 lg:px-1">
          <h2 className="text-4xl font-outfit font-light mb-4 text-primary1">YOUR PROFILE</h2>
          <p className="font-outfit font-light text-2xl">{user ? `${user.first_name} ${user.last_name}` : "Loading..."}</p>
          <div className="flex items-center gap-6 xl:gap-20 w-full">
            <p className="text-hcolor font-outfit font-light truncate">{user ? user.email_id : "Loading..."}</p>
            {/* {emailVerified ? (
              <span className="text-sm text-hcolor font-outfit font-light">Verified</span>
            ) : (
              <button onClick={handleVerifyClick} className="text-sm underline text-hcolor font-outfit font-light">
                Verify your E-mail
              </button>
            )} */}
          </div>
          <p className="text-hcolor font-outfit font-light">{user ? user.mobile_number : "Loading..."}</p>
          <p className="text-hcolor font-outfit font-light">{fullAddress}</p>

          <div className="mt-6">
            {["Addresses", "Track Order", "Coupons & Offers", "Logout"].map((link, index) => (
              <p
                key={index}
                className="text-primary1 underline mb-2 cursor-pointer font-outfit font-light"
                onClick={() => {
                  if (link === "Logout") {
                    setShowLogoutPopup(true);
                  } else if (link === "Track Order") {
                    setShowTrackOrderPopup(true);
                  } else {
                    setActiveSection(link);
                  }
                }}
              >
                {link}
              </p>
            ))}
          </div>
        </div>

        {/* Right Section */}
        <div className="lg:w-2/3 w-full bg-tertiary px-10">
          <div className="flex items-center mb-4">
            {activeSection !== "YOUR ORDERS" && (
              <button onClick={() => setActiveSection("YOUR ORDERS")} className="text-primary1 font-outfit font-light flex items-center">
                <img src={icons.back} alt="Back" className="w-9 h-8" />
              </button>
            )}
            <h2 className="text-3xl font-light font-outfit lg:ml-7">{activeSection}</h2>
          </div>

          <div className="w-full text-hcolor h-[85%]">
            {activeSection === "YOUR ORDERS" ? (
              <YourOrders orders={orders} />
            ) : activeSection === "Addresses" ? (
              <AddressesSection
                handleInputChange={handleInputChange}
                handleFormSubmit={handleFormSubmit}
                address={address}
                isEditable={isEditable}
              />
            ) : activeSection === "Coupons & Offers" ? (
              <CouponsSection />
            ) : (
              <p>Content for {activeSection}</p>
            )}
          </div>
        </div>
      </div>

      {showErrorPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-5 rounded-lg shadow-lg">
            <p className="text-red-500">Error: Unable to track order. Please try again.</p>
            <button onClick={() => setShowErrorPopup(false)} className="mt-2 px-4 py-2 bg-primary1 text-white rounded">
              Close
            </button>
          </div>
        </div>
      )}

      {showLogoutPopup && <LogOutPopup setShowLogoutPopup={setShowLogoutPopup} />}
      {showTrackOrderPopup && (
        <TrackOrderPopup
          isOpen={showTrackOrderPopup}
          onClose={() => setShowTrackOrderPopup(false)}
          setShowErrorPopup={setShowErrorPopup}
        />
      )}
      {showOtpModal && <OtpPopup show={showOtpModal} onClose={() => setShowOtpModal(false)} onVerify={setEmailVerified} />}

      {/* Footer Doodles */}
      <div className="relative">
        <FooterDoodles />
        <div className="hidden lg:block absolute lg:px-20 xl:px-32 left-0 top-0 h-full w-full pointer-events-none">
          <div className="w-[2px] bg-secondary absolute left-1/3 top-0 bottom-0"></div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ProfilePage;